import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { GET_ONBOARDING_EMPLOYEES_NAME_LIST } from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { ApiError, getApi } from "../../../../utils/api.ts";
import type { SelectOption } from "../../../../components/ui/select/select.tsx";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { SQE_ONBOARDING } from "../../onboarding/sqe/sqe-onboarding-api.ts";

const nameRowSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
});

function parseNameListBody(raw: unknown): SelectOption[] {
  const tryArray = (v: unknown): SelectOption[] => {
    const parsed = z.array(nameRowSchema).safeParse(v);
    if (!parsed.success) return [];
    return parsed.data.map((r) => ({
      value: String(r.id),
      label: r.name,
    }));
  };
  if (raw != null && typeof raw === "object") {
    const d = (raw as Record<string, unknown>).data;
    if (Array.isArray(d)) return tryArray(d);
  }
  if (Array.isArray(raw)) return tryArray(raw);
  return [];
}

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

export function useOnboardingEmployeeNames() {
  const isSqe = useSqeLabs();
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getApi<unknown>(
        isSqe ? `${SQE_ONBOARDING}/employees/name` : GET_ONBOARDING_EMPLOYEES_NAME_LIST,
        accessToken(),
        "json",
        signal,
      );
      setOptions(parseNameListBody(data));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to load employee names.";
      setError(msg);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [isSqe]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  return { options, loading, error, refetch: load };
}
